import type { FastifyRequest, FastifyReply } from 'fastify'
import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import { createLogger } from '../../common/utils/logger.js'
import { SeasonService } from './season.service.js'
import EventEmitter from 'node:events'

const logger = createLogger('SeasonController')

export class SeasonController {
  private readonly seasonService: SeasonService

  constructor(firestore: Firestore, redis: RedisClient) {
    const events = new EventEmitter()
    this.seasonService = new SeasonService(firestore, redis, events)
  }

  /**
   * GET /api/gamification/season
   * Get active season configuration and user's progress
   */
  async getSeasonProgress(
    request: FastifyRequest,
    reply: FastifyReply
  ): Promise<void> {
    try {
      const userId = request.user?.sub
      if (!userId) {
        reply.code(401).send({ error: 'Unauthorized' })
        return
      }

      const progress = await this.seasonService.getUserSeasonProgress(userId)

      reply.send({
        season: progress.seasonConfig,
        progress: {
          level: progress.level,
          xp: progress.xp,
          totalXp: progress.totalXp,
          claimedRewards: progress.claimedRewards,
          lastVanAction: progress.lastVanAction
        }
      })
    } catch (error) {
      logger.error('Error getting season progress', { error, userId: request.user?.sub })
      reply.code(500).send({ error: 'Failed to get season progress' })
    }
  }

  /**
   * GET /api/gamification/season/rewards/unclaimed
   * Get rewards user hasn't claimed yet
   */
  async getUnclaimedRewards(
    request: FastifyRequest,
    reply: FastifyReply
  ): Promise<void> {
    try {
      const userId = request.user?.sub
      if (!userId) {
        reply.code(401).send({ error: 'Unauthorized' })
        return
      }

      const unclaimedRewards = await this.seasonService.getUnclaimedRewards(userId)

      reply.send({ rewards: unclaimedRewards })
    } catch (error) {
      logger.error('Error getting unclaimed rewards', { error, userId: request.user?.sub })
      reply.code(500).send({ error: 'Failed to get unclaimed rewards' })
    }
  }

  /**
   * POST /api/gamification/season/rewards/claim
   * Claim reward at specific level
   * 
   * Body: { level: number }
   */
  async claimReward(
    request: FastifyRequest<{ Body: { level: number } }>,
    reply: FastifyReply
  ): Promise<void> {
    try {
      const userId = request.user?.sub
      if (!userId) {
        reply.code(401).send({ error: 'Unauthorized' })
        return
      }

      const { level } = request.body
      if (!level || typeof level !== 'number') {
        reply.code(400).send({ error: 'Invalid level' })
        return
      }

      const result = await this.seasonService.claimReward(userId, level)

      reply.send(result)
    } catch (error) {
      logger.error('Error claiming reward', { error, userId: request.user?.sub, level: request.body?.level })
      const message = error instanceof Error ? error.message : 'Failed to claim reward'
      const statusCode = message.includes('not reached') || message.includes('already claimed') || message.includes('No reward') ? 400 : 500
      reply.code(statusCode).send({ error: message })
    }
  }

  /**
   * GET /api/gamification/season/van-actions
   * Get available van bonus actions with cooldown status
   */
  async getVanActions(
    request: FastifyRequest,
    reply: FastifyReply
  ): Promise<void> {
    try {
      const userId = request.user?.sub
      if (!userId) {
        reply.code(401).send({ error: 'Unauthorized' })
        return
      }

      const actions = await this.seasonService.getVanActions(userId)

      reply.send({ actions })
    } catch (error) {
      logger.error('Error getting van actions', { error, userId: request.user?.sub })
      reply.code(500).send({ error: 'Failed to get van actions' })
    }
  }

  /**
   * POST /api/gamification/season/van-action
   * Perform van bonus action (daily XP boost)
   * 
   * Body: { actionId: string }
   */
  async performVanAction(
    request: FastifyRequest<{ Body: { actionId: string } }>,
    reply: FastifyReply
  ): Promise<void> {
    try {
      const userId = request.user?.sub
      if (!userId) {
        reply.code(401).send({ error: 'Unauthorized' })
        return
      }

      const { actionId } = request.body
      if (!actionId || typeof actionId !== 'string') {
        reply.code(400).send({ error: 'Invalid action ID' })
        return
      }

      const result = await this.seasonService.performVanAction(userId, actionId)

      reply.send(result)
    } catch (error) {
      logger.error('Error performing van action', { error, userId: request.user?.sub, actionId: request.body?.actionId })
      const message = error instanceof Error ? error.message : 'Failed to perform van action'
      const statusCode = message.includes('cooldown') || message.includes('not found') ? 400 : 500
      reply.code(statusCode).send({ error: message })
    }
  }

  /**
   * GET /api/gamification/season/leaderboard
   * Get season leaderboard (top users by XP)
   * 
   * Query: { seasonId?: string, limit?: number, lastUserId?: string }
   */
  async getSeasonLeaderboard(
    request: FastifyRequest<{ Querystring: { seasonId?: string; limit?: string; lastUserId?: string } }>,
    reply: FastifyReply
  ): Promise<void> {
    try {
      const { seasonId, limit: limitStr, lastUserId } = request.query

      // Get active season if no seasonId provided
      let targetSeasonId = seasonId
      if (!targetSeasonId) {
        const activeSeason = await this.seasonService.getActiveSeason()
        if (!activeSeason) {
          reply.code(404).send({ error: 'No active season' })
          return
        }
        targetSeasonId = activeSeason.seasonId
      }

      const limit = limitStr ? Number.parseInt(limitStr, 10) : 50

      const leaderboard = await this.seasonService.getSeasonLeaderboard(
        targetSeasonId,
        limit,
        lastUserId
      )

      reply.send({
        seasonId: targetSeasonId,
        leaderboard
      })
    } catch (error) {
      logger.error('Error getting season leaderboard', { error, seasonId: request.query?.seasonId, userId: request.user?.sub })
      reply.code(500).send({ error: 'Failed to get season leaderboard' })
    }
  }
}
